import { CreditCard, Lock, User, CircleHelp, Plus } from 'lucide-react'
import { formatCardNumber, formatCardExpiry, formatCardCvc } from '@utils/cardFormatting'
import { getCardIconPath } from '@utils/image'
import { OrderPaymentOptions } from './checkoutHelpers'
import type { PaymentSource } from './checkoutHelpers'

type Props = {
  creditCard: any
  cardErrors: Record<string, any>
  setCardValue: (field: any, value: any, options?: any) => void
  cardBrand: string
  showSourceEdit: boolean
  orderPaymentSource: OrderPaymentOptions
  setOrderPaymentSource: (option: OrderPaymentOptions) => void
  paymentSource: PaymentSource | null
  paymentSourceOptions: PaymentSource[]
  isSessionUserKnown: () => boolean
  toggleEditing: (name: 'bill' | 'ship' | 'source') => void
  showSatisfactionGuarantee: boolean
  guaranteeDays: number | string
}

/**
 * Payment method. A returning customer can pay with a stored source or add a
 * new card; only the 'new' branch mounts the card fields.
 */
const CheckoutPaymentSection = ({
  creditCard,
  cardErrors,
  setCardValue,
  cardBrand,
  showSourceEdit,
  orderPaymentSource,
  setOrderPaymentSource,
  paymentSource,
  paymentSourceOptions,
  isSessionUserKnown,
  showSatisfactionGuarantee,
  guaranteeDays,
}: Props) => (
  <>
    <div className="form-section">
      <h4>Payment Method</h4>

      {isSessionUserKnown() && paymentSourceOptions.length > 0 && (
        <div className="flex flex-col gap-3 mb-4">
          <label
            htmlFor="existingCard"
            className="payment-card-option"
            data-selected={orderPaymentSource === OrderPaymentOptions.Default || undefined}>
            <input
              className="form-check-input"
              type="radio"
              id="existingCard"
              value={OrderPaymentOptions.Default}
              checked={orderPaymentSource === OrderPaymentOptions.Default}
              onChange={e => setOrderPaymentSource(e.target.value as OrderPaymentOptions)}
            />
            <img
              src={getCardIconPath(paymentSource?.type || 'unknown')}
              alt={paymentSource?.type || 'Card'}
              className="w-10 h-auto"
            />
            <div className="flex-1 min-w-0">
              <div className="font-semibold" style={{ color: 'var(--text-color)' }}>
                **** **** **** {paymentSource?.last4}
              </div>
              <div className="text-sm" style={{ color: 'var(--light-text-color)' }}>
                {(paymentSource?.type || 'Card').charAt(0).toUpperCase() + (paymentSource?.type || 'card').slice(1)} ·
                Expires {paymentSource?.expDate}
              </div>
            </div>
          </label>

          <button
            type="button"
            className="payment-card-add"
            onClick={() => setOrderPaymentSource(OrderPaymentOptions.New)}>
            <Plus size={16} />
            <span>Add New Card</span>
          </button>
        </div>
      )}

      {showSourceEdit && (
        <div className="credit-card-form">
          {/* Name on Card — with user icon prefix */}
          <div className="form-group">
            <div className="payment-input-wrap">
              <span className="payment-input-prefix">
                <User size={18} />
              </span>
              <input
                type="text"
                className={`form-control payment-input ${cardErrors.title ? 'is-invalid' : ''}`}
                placeholder="Name on Card"
                value={creditCard.title || ''}
                onChange={e => setCardValue('title', e.target.value, { shouldValidate: true })}
              />
            </div>
            {cardErrors.title && <div className="invalid-feedback">{cardErrors.title.message}</div>}
          </div>

          {/* Card Number — auto-formatted with spaces + card icon + lock suffix */}
          <div className="form-group">
            <div className="payment-input-wrap">
              <span className="payment-input-prefix">
                <CreditCard size={18} />
              </span>
              <input
                id="number"
                type="tel"
                inputMode="numeric"
                className={`form-control payment-input payment-input-both ${cardErrors.number ? 'is-invalid' : ''}`}
                placeholder="1234 1234 1234 1234"
                maxLength={23}
                value={creditCard.number || ''}
                onChange={e => setCardValue('number', formatCardNumber(e.target.value), { shouldValidate: true })}
              />
              <span className="payment-input-suffix text-gray-400 cursor-help" role="tooltip">
                <Lock size={16} />
                <span className="tooltip-bubble">All payments are safe and secure</span>
              </span>
            </div>
            {cardErrors.number && <div className="invalid-feedback">{cardErrors.number.message}</div>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
            <div className="form-group">
              <input
                id="expiry"
                type="tel"
                inputMode="numeric"
                className={`form-control ${cardErrors.expiry ? 'is-invalid' : ''}`}
                placeholder="MM/YYYY"
                maxLength={7}
                value={creditCard.expiry || ''}
                onChange={e => setCardValue('expiry', formatCardExpiry(e.target.value), { shouldValidate: true })}
              />
              <input
                type="hidden"
                id="expiry_month"
                readOnly
                value={(() => {
                  const m = (creditCard.expiry || '').split('/')[0]?.trim() || ''
                  return /^\d+$/.test(m) ? m.padStart(2, '0') : m
                })()}
              />
              <input
                type="hidden"
                id="expiry_year"
                readOnly
                value={(() => {
                  let y = (creditCard.expiry || '').split('/')[1]?.trim() || ''
                  if (y.length === 2 && /^\d+$/.test(y)) {
                    y = new Date().getFullYear().toString().slice(0, 2) + y
                  }
                  return y
                })()}
              />
              {cardErrors.expiry && <div className="invalid-feedback">{cardErrors.expiry.message}</div>}
            </div>
            <div className="form-group">
              <div className="payment-input-wrap">
                <input
                  id="cvv_code"
                  type="tel"
                  inputMode="numeric"
                  className={`form-control payment-input-suffix-pad ${cardErrors.cvv ? 'is-invalid' : ''}`}
                  placeholder={cardBrand === 'amex' ? 'CVV (4 digits)' : 'CVV'}
                  maxLength={cardBrand === 'amex' ? 4 : 3}
                  value={creditCard.cvv || ''}
                  onChange={e =>
                    setCardValue('cvv', formatCardCvc(e.target.value, cardBrand), { shouldValidate: true })
                  }
                />
                <span className="payment-input-suffix text-gray-400 cursor-help" role="tooltip">
                  <CircleHelp size={16} />
                  <span className="tooltip-bubble">3 or 4 digits usually found on the back of your card</span>
                </span>
              </div>
              {cardErrors.cvv && <div className="invalid-feedback">{cardErrors.cvv.message}</div>}
            </div>
          </div>
        </div>
      )}
    </div>

    {showSatisfactionGuarantee && (
      <div className="icon-guarantee">
        <div className="logo">
          <img src="/assets/images/guarantee-icon.png" alt="Satisfaction Guarantee" className="max-w-full h-auto" />
        </div>
        <div>
          <h5 className="satisfaction-text">{guaranteeDays} Day Satisfaction Guarantee</h5>
          <div className="guarantee-text">
            We stand behind our products with a {guaranteeDays} day satisfaction guarantee. If you're not completely
            satisfied, return your purchase for a full refund.
          </div>
        </div>
      </div>
    )}
  </>
)

export default CheckoutPaymentSection
